import React, {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';
import {
  initializeExoSkeletonRuntimeValues,
  isInExoSkeleton,
  type InitializeExoSkeletonRuntimeValuesOptions,
} from '@adsk/exo-skeleton-api';

export interface RemotesContextValue {
  isLoading: boolean;
}

const RemotesContext = getOrCreateGlobalContext<RemotesContextValue>(
  CONTEXT_NAMES.ExoSkeletonRemotesContext,
);

export const useRemotes = (): RemotesContextValue => useContext(RemotesContext);

const useRemotesInitialization = (initializeCallback: () => Promise<void>) => {
  const [isLoading, setIsLoading] = useState(!isInExoSkeleton());
  const [error, setError] = useState<Error | null>(null);

  useLayoutEffect(() => {
    async function initialize() {
      try {
        await initializeCallback();
      } catch (err: unknown) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    }

    initialize();
  }, [initializeCallback]);

  return { isLoading, error };
};

export interface RemotesProviderProps extends InitializeExoSkeletonRuntimeValuesOptions {
  children: React.ReactNode;
}

const defaultGetRemoteOverride: RemotesProviderProps['getRemoteOverride'] = (
  scope,
) => {
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.has(scope)) {
    return {
      url: urlParams.get(scope)!,
    };
  } else {
    return null;
  }
};

export const RemotesProvider: React.FC<RemotesProviderProps> = ({
  children,
  environmentConfig: { env, region } = {},
  getRemoteOverride = defaultGetRemoteOverride,
  transformRemotesUrl,
  transformRemoteEntryUrl,
}) => {
  const initializeCallback = useCallback(
    () =>
      initializeExoSkeletonRuntimeValues({
        environmentConfig: { env, region },
        getRemoteOverride,
        transformRemotesUrl,
        transformRemoteEntryUrl,
      }),
    [
      env,
      region,
      getRemoteOverride,
      transformRemotesUrl,
      transformRemoteEntryUrl,
    ],
  );

  const { isLoading, error } = useRemotesInitialization(initializeCallback);

  const value = useMemo(
    () => ({
      isLoading,
    }),
    [isLoading],
  );

  if (error) {
    throw error;
  }

  return (
    <RemotesContext.Provider value={value}>{children}</RemotesContext.Provider>
  );
};
